//client/src/pages/FarmDetail.tsx
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabaseClient.ts";
import AddCropHealthReportModal from "../components/AddCropHealthReportModal.tsx";
import EditCropHealthReportModal from "../components/EditCropHealthReportModal.tsx";
import EditFarmModal from "../components/EditFarmModal.tsx";
import { ArrowLeft, MapPin, Maximize, Calendar, PlusCircle, Pencil, Trash2 } from "lucide-react";
import type { User } from '../types.ts'; 
// Utility for formatting dates nicely
function formatDateTime(dateStr: string) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit"
  });
}

const statusStyles: Record<string, string> = {
  Healthy: "bg-green-100 text-green-800",
  "Needs Attention": "bg-yellow-100 text-yellow-800",
  Diseased: "bg-red-100 text-red-800",
  "Pest Detected": "bg-orange-100 text-orange-800"
};

// Define props for FarmDetail
interface FarmDetailProps {
  user: User | null; // <-- ADD THIS INTERFACE AND USER PROP
}

export default function FarmDetail({ user }: FarmDetailProps) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [farm, setFarm] = useState<any>(null);
  const [reports, setReports] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddReport, setShowAddReport] = useState(false);
 

  // For editing reports
  const [editingReport, setEditingReport] = useState<any>(null);
  // For deleting reports
  const [deletingReport, setDeletingReport] = useState<any>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // For editing farm
  const [editingFarm, setEditingFarm] = useState(false);
  // For deleting farm
  const [showDeleteFarm, setShowDeleteFarm] = useState(false);
  const [deleteFarmLoading, setDeleteFarmLoading] = useState(false);
  const [deleteFarmError, setDeleteFarmError] = useState<string | null>(null);


  useEffect(() => {
    async function fetchFarm() {
      setLoading(true);
      const { data } = await supabase
        .from("farms")
        .select("*")
        .eq("id", id)
        .single();
      setFarm(data);
      setLoading(false);
    }
    async function fetchReports() {
      const { data } = await supabase
        .from("crop_health_reports")
        .select("*")
        .eq("farm_id", id)
        .order("created_at", { ascending: false });
      setReports(data || []);
    }
    if (id) {
      fetchFarm();
      fetchReports();
    }
    // Re-fetch reports/farm when a new one is added or edited or deleted
  }, [id, showAddReport, editingReport, deletingReport, editingFarm]);

  // Handlers for Edit/Update
  function handleReportUpdated() {
    setEditingReport(null);
  }
  function handleFarmUpdated() {
    setEditingFarm(false);
  }

  // Handlers for Delete Report
  async function handleConfirmDelete() {
    if (!deletingReport) return;
    setDeleteLoading(true);
    setDeleteError(null);
    const { error } = await supabase
      .from("crop_health_reports")
      .delete()
      .eq("id", deletingReport.id);
    setDeleteLoading(false);
    if (error) {
      setDeleteError("Failed to delete: " + error.message);
    } else {
      setDeletingReport(null);
    }
  }

  // Handlers for Delete Farm
  async function handleConfirmDeleteFarm() {
    if (!farm) return;
    setDeleteFarmLoading(true);
    setDeleteFarmError(null);

    // Optionally: delete all crop_health_reports for this farm first (cascade)
    // await supabase.from("crop_health_reports").delete().eq("farm_id", farm.id);

    const { error } = await supabase
      .from("farms")
      .delete()
      .eq("id", farm.id);
    setDeleteFarmLoading(false);
    if (error) {
      setDeleteFarmError("Failed to delete farm: " + error.message);
    } else {
      setShowDeleteFarm(false);
      navigate("/"); // Redirect to dashboard after deletion
    }
  }

  if (loading) return <div className="p-6 text-lg text-center text-gray-600">Loading farm details...</div>;
  if (!farm) return <div className="p-6 text-lg text-center text-red-600">Farm not found.</div>;

  return (
    <div className="max-w-4xl p-6 mx-auto bg-white rounded-lg shadow-xl">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="text-green-700 flex items-center gap-1 mb-6 px-3 py-1.5 rounded-md border border-green-300 hover:bg-green-50 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        <span>Back to Farms</span>
      </button>

      {/* Farm Details Card */}
      <div className="p-6 mb-8 border border-green-200 rounded-lg bg-green-50">
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <h1 className="mb-2 text-4xl font-extrabold text-green-800 md:mb-0">{farm.name}</h1>
          {user && (
            <div className="flex gap-2">
              <button
                type="button"
                className="inline-flex items-center px-4 py-1.5 text-blue-700 bg-blue-100 rounded hover:bg-blue-200 transition text-sm"
                onClick={() => setEditingFarm(true)}
              >
                <Pencil className="w-4 h-4 mr-1" /> Edit Farm
              </button>
              <button
                type="button"
                className="inline-flex items-center px-4 py-1.5 text-red-700 bg-red-100 rounded hover:bg-red-200 transition text-sm"
                onClick={() => setShowDeleteFarm(true)}
              >
                <Trash2 className="w-4 h-4 mr-1" /> Delete Farm
              </button>
            </div>
          )}
        </div>
        <div className="grid grid-cols-1 gap-4 mt-4 text-gray-700 md:grid-cols-2">
          <div className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-green-600" />
            <span className="font-semibold">Location:</span> {farm.location || "—"}
          </div>
          <div className="flex items-center gap-2">
            <Maximize className="w-5 h-5 text-green-600" />
            <span className="font-semibold">Area:</span> {farm.area} acres
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Calendar className="w-4 h-4 text-green-600" />
            <span className="font-semibold">Created:</span>{" "}
            {formatDateTime(farm.created_at)}
          </div>
        </div>
      </div>

      {/* Crop Health Reports Section Header and Add Button */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Crop Health Reports</h2>
        {user && (
          <button
            type="button"
            className="inline-flex items-center gap-3 px-4 py-2 text-sm font-medium text-white bg-green-900 rounded-md h-9 hover:bg-green-700"
            onClick={() => setShowAddReport(true)}
          >
            <PlusCircle className="w-5 h-5" />
            Add Report
          </button>
        )}
      </div>

      {reports.length === 0 ? (
        <div className="p-8 italic text-center text-gray-500 border rounded-lg bg-gray-50">No reports yet for this farm.</div>
      ) : (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-2">
          {reports.map((report) => (
            <div
              key={report.id}
              className="p-5 transition-shadow duration-200 bg-white border border-gray-100 shadow-md rounded-xl hover:shadow-lg"
            >
              <div className="flex items-center justify-between pb-2 mb-3 border-b border-gray-100">
                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold ${statusStyles[report.status] || "bg-gray-100 text-gray-800"}`}
                >
                  {report.status}
                </span>
                <span className="text-xs text-gray-500">
                  {formatDateTime(report.created_at)}
                </span>
              </div>
              {report.image_url && (
                <div className="flex justify-center my-3">
                  <img
                    src={report.image_url}
                    alt="Crop health"
                    className="object-cover w-full h-48 border border-gray-200 rounded-lg sm:h-56"
                  />
                </div>
              )}
              {report.notes && (
                <div className="mt-3 text-sm text-gray-700">{report.notes}</div>
              )}
              {user && (
                <div className="flex justify-end gap-2 mt-4">
                  <button
                    type="button"
                    className="inline-flex items-center px-3 py-1 text-xs text-blue-700 transition bg-blue-100 rounded hover:bg-blue-200"
                    onClick={() => setEditingReport(report)}
                  >
                    <Pencil className="w-4 h-4 mr-1" /> Edit
                  </button>
                  <button
                    type="button"
                    className="inline-flex items-center px-3 py-1 text-xs text-red-700 transition bg-red-100 rounded hover:bg-red-200"
                    onClick={() => setDeletingReport(report)}
                  >
                    <Trash2 className="w-4 h-4 mr-1" /> Delete
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add Report Modal */}
      {user && (
        <AddCropHealthReportModal
          open={showAddReport}
          onClose={() => setShowAddReport(false)}
          onReportAdded={() => setShowAddReport(false)}
          userId={user?.id}
          farms={farm ? [{ id: farm.id, name: farm.name }] : []}
        />
      )}

      {/* Edit Report Modal */}
      {user && editingReport && (
        <EditCropHealthReportModal
          open={!!editingReport}
          onClose={() => setEditingReport(null)}
          report={editingReport}
          onReportUpdated={handleReportUpdated}
        />
      )}

      {/* Edit Farm Modal */}
      {user && editingFarm && (
        <EditFarmModal
          open={editingFarm}
          onClose={() => setEditingFarm(false)}
          farm={farm}
          onFarmUpdated={handleFarmUpdated}
        />
      )}

      {/* Delete Report Confirmation */}
      {user && deletingReport && (
        <div className="fixed inset-0 z-30 flex items-center justify-center bg-black bg-opacity-40">
          <div className="relative w-full max-w-sm p-6 bg-white rounded-lg shadow-lg">
            <h3 className="mb-4 text-lg font-semibold text-red-600">Delete Report?</h3>
            <p className="mb-4">Are you sure you want to delete this report? This action cannot be undone.</p>
            {deleteError && <div className="mb-2 text-red-600">{deleteError}</div>}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                onClick={() => setDeletingReport(null)}
                disabled={deleteLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-4 py-2 font-semibold text-white bg-red-600 rounded hover:bg-red-700"
                onClick={handleConfirmDelete}
                disabled={deleteLoading}
              >
                {deleteLoading ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Farm Confirmation */}
      {user && showDeleteFarm && (
        <div className="fixed inset-0 z-30 flex items-center justify-center bg-black bg-opacity-40">
          <div className="relative w-full max-w-sm p-6 bg-white rounded-lg shadow-lg">
            <h3 className="mb-4 text-lg font-semibold text-red-600">Delete Farm?</h3>
            <p className="mb-4">Are you sure you want to delete this farm? This will remove all associated crop health reports. This action cannot be undone.</p>
            {deleteFarmError && <div className="mb-2 text-red-600">{deleteFarmError}</div>}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                onClick={() => setShowDeleteFarm(false)}
                disabled={deleteFarmLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-4 py-2 font-semibold text-white bg-red-600 rounded hover:bg-red-700"
                onClick={handleConfirmDeleteFarm}
                disabled={deleteFarmLoading}
              >
                {deleteFarmLoading ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}